import React from 'react';
import { Box, Divider, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { formatCurrency } from '../../../utils/formatters';

export type LiquidacionesComisionesResumenProps = {
  cantidad: number;
  totalLiquidado: number;
  promedio: number;
  vendedores: number;
  proveedores: number;
};

type ResumenItem = {
  key: string;
  icon: React.ReactNode;
  iconColor: string;
  value: string;
  valueColor?: string;
  label: string;
};

const LiquidacionesComisionesResumen: React.FC<LiquidacionesComisionesResumenProps> = ({
  cantidad,
  totalLiquidado,
  promedio,
  vendedores,
  proveedores,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const items: ResumenItem[] = [
    {
      key: 'cantidad',
      icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 20 }} />,
      iconColor: '#757575',
      value: String(cantidad),
      label: cantidad === 1 ? 'liquidación' : 'liquidaciones',
    },
    {
      key: 'total',
      icon: <AttachMoneyIcon sx={{ fontSize: 20 }} />,
      iconColor: '#2e7d32',
      value: formatCurrency(totalLiquidado),
      valueColor: '#2e7d32',
      label: 'total liquidado',
    },
    {
      key: 'promedio',
      icon: <PriceCheckIcon sx={{ fontSize: 20 }} />,
      iconColor: '#00897b',
      value: formatCurrency(promedio),
      valueColor: '#00897b',
      label: 'promedio',
    },
    {
      key: 'vendedores',
      icon: <BadgeOutlinedIcon sx={{ fontSize: 20 }} />,
      iconColor: '#f9a825',
      value: String(vendedores),
      label: vendedores === 1 ? 'vendedor' : 'vendedores',
    },
    {
      key: 'proveedores',
      icon: <StorefrontOutlinedIcon sx={{ fontSize: 20 }} />,
      iconColor: '#e53935',
      value: String(proveedores),
      label: proveedores === 1 ? 'proveedor' : 'proveedores',
    },
  ];

  return (
    <Box
      sx={{
        mb: 3,
        py: { xs: 1.25, sm: 1.5 },
        px: { xs: 1.5, sm: 2 },
        bgcolor: '#fff',
        borderRadius: 2,
        border: '1px solid #e0e0e0',
        overflowX: 'auto',
      }}
      role="status"
      aria-label="Resumen de liquidaciones de comisiones"
    >
      <Stack
        direction={isMobile ? 'column' : 'row'}
        alignItems={isMobile ? 'stretch' : 'center'}
        divider={
          <Divider
            orientation={isMobile ? 'horizontal' : 'vertical'}
            flexItem
            sx={{ borderColor: '#e0e0e0' }}
          />
        }
        spacing={0}
      >
        {items.map((item) => (
          <Stack
            key={item.key}
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ flex: 1, px: { xs: 0.5, sm: 1.5 }, py: { xs: 1, sm: 0.5 }, minWidth: 0 }}
          >
            <Box sx={{ color: item.iconColor, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              {item.icon}
            </Box>
            <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ minWidth: 0, flexWrap: 'wrap' }}>
              <Typography
                component="span"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  lineHeight: 1.2,
                  color: item.valueColor ?? '#424242',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.value}
              </Typography>
              <Typography
                component="span"
                variant="body2"
                sx={{ color: '#9e9e9e', fontWeight: 400, whiteSpace: 'nowrap' }}
              >
                {item.label}
              </Typography>
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

export default LiquidacionesComisionesResumen;
